// What the page tells the bridge unasked: `{ type: 'event', name, payload,
// at }` for the review's state, the store's revision, the scan queue's
// progress, the sync's phase, a toast shown and a puzzle solved or failed.
// Each summary is small and deduplicated (the same summary twice sends
// nothing); the store's is debounced, since a scan pass bumps its revision
// many times a second. The pure parts, the summaries and the dedupe, are
// tested; the subscriptions are wired in `index.tsx`.
import { positionAt, type ReviewState } from '@chess/api-review';
import type { StoreState } from '@chess/api-model';
import type { ScanProgress } from '@chess/api-scan';
import type { SyncState } from '@chess/api-sync';
import type { HostNotification } from './host.ts';

export type EventName = 'review' | 'store' | 'scan' | 'sync' | 'toast' | 'puzzle';

export interface BridgeEvent {
  type: 'event';
  name: EventName;
  payload: unknown;
  at: number;
}

export const STORE_DEBOUNCE_MS = 200;

export interface ReviewSummary {
  gameId?: string;
  status: ReviewState['status'];
  ply: number;
  /** Moves of the reader's own line; 0 while they are on the game. */
  exploring: number;
  browse?: number;
  /** The position on the board (the one looked back at, else the head). */
  fen?: string;
}

export function reviewSummary(state: ReviewState): ReviewSummary {
  const summary: ReviewSummary = { status: state.status, ply: state.ply, exploring: state.exploring?.moves.length ?? 0 };
  if (state.gameId !== undefined) summary.gameId = state.gameId;
  if (state.browse !== undefined) summary.browse = state.browse;
  const fen = positionAt(state);
  if (fen !== undefined) summary.fen = fen;
  return summary;
}

export interface StoreSummary {
  status: StoreState['status'];
  revision: number;
  /** The period's games and how many are scanned; the loaded list's count before the period is known. */
  games: number;
  scanned: number;
}

export function storeSummary(state: StoreState): StoreSummary {
  return {
    status: state.status,
    revision: state.revision,
    games: state.period?.games ?? state.games.length,
    scanned: state.period?.scanned ?? 0,
  };
}

export interface ScanSummary {
  running: boolean;
  done: number;
  total: number;
  current?: string;
}

export function scanSummary(progress: ScanProgress): ScanSummary {
  const summary: ScanSummary = { running: progress.running, done: progress.done, total: progress.total };
  if (progress.current !== undefined) summary.current = progress.current;
  return summary;
}

export interface SyncSummary {
  phase: SyncState['phase'];
  imported?: number;
  error?: string;
}

export function syncSummary(state: SyncState): SyncSummary {
  const summary: SyncSummary = { phase: state.phase };
  if (state.imported !== undefined) summary.imported = state.imported;
  if (state.error !== undefined) summary.error = state.error;
  return summary;
}

export interface ToastSummary {
  id: number;
  /** The message, when it can be read: a string content, or a toast element's live message. */
  message?: string;
  kind?: string;
  title?: string;
  pilet: string;
}

/**
 * The toasts that appeared since `seen`: the `toast` API has no observer, so
 * the bridge watches the host's notification list, which every toast goes
 * through. The message is read from a string content, or from the element's
 * `live.message` / `message` prop as shared-toast renders it; a content of
 * another shape gives a toast without a message.
 */
export function newToasts(notifications: readonly HostNotification[], seen: number): { toasts: ToastSummary[]; seen: number } {
  const toasts: ToastSummary[] = [];
  let last = seen;
  for (const n of notifications) {
    if (n.id <= seen) continue;
    last = Math.max(last, n.id);
    const summary: ToastSummary = { id: n.id, pilet: n.pilet };
    const read = readToast(n.content);
    if (read.message !== undefined) summary.message = read.message;
    if (read.kind !== undefined) summary.kind = read.kind;
    if (n.options.title !== undefined) summary.title = n.options.title;
    toasts.push(summary);
  }
  return { toasts, seen: last };
}

function readToast(content: unknown): { message?: string; kind?: string } {
  if (typeof content === 'string') return { message: content };
  if (!content || typeof content !== 'object') return {};
  const props = (content as { props?: unknown }).props;
  if (!props || typeof props !== 'object') return {};
  const p = props as { live?: { message?: unknown }; message?: unknown; kind?: unknown };
  const message = typeof p.live?.message === 'string' ? p.live.message : typeof p.message === 'string' ? p.message : undefined;
  const kind = typeof p.kind === 'string' ? p.kind : undefined;
  return { message, kind };
}

/** True when two summaries are the same data; the cheap way, since summaries are small and ordered. */
export function sameSummary(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Sends each event once per change: a payload equal to the last one sent
 * under the same name is skipped. `resend` sends the last payload of every
 * stateful name again, for a bridge that has just connected.
 */
export class Deduper {
  private readonly last = new Map<EventName, unknown>();
  private readonly send: (event: BridgeEvent) => void;
  private readonly now: () => number;
  constructor(send: (event: BridgeEvent) => void, now: () => number = () => Date.now()) {
    this.send = send;
    this.now = now;
  }

  /** Sends when the payload differs from the last under `name`; returns whether it went. */
  emit(name: EventName, payload: unknown): boolean {
    if (this.last.has(name) && sameSummary(this.last.get(name), payload)) return false;
    this.last.set(name, payload);
    this.send({ type: 'event', name, payload, at: this.now() });
    return true;
  }

  /** Sends regardless (a toast or a puzzle: each one counts), without remembering it. */
  push(name: EventName, payload: unknown): void {
    this.send({ type: 'event', name, payload, at: this.now() });
  }

  /** The last payload of each stateful name again, in a fixed order. */
  resend(): void {
    for (const name of ['sync', 'store', 'scan', 'review'] as const) {
      if (this.last.has(name)) this.send({ type: 'event', name, payload: this.last.get(name), at: this.now() });
    }
  }
}

/** Calls `fn` once `ms` after the last call; the pending call is dropped on the next. */
export function debounce(fn: () => void, ms: number, timers = { setTimeout: (f: () => void, t: number) => setTimeout(f, t) as unknown, clearTimeout: (h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>) }): () => void {
  let handle: unknown;
  return () => {
    if (handle !== undefined) timers.clearTimeout(handle);
    handle = timers.setTimeout(() => {
      handle = undefined;
      fn();
    }, ms);
  };
}
